import moment from 'moment';
// eslint-disable-next-line import/no-unresolved
import AppConfig from 'appconfig';
import XHRFetch from './XHRFetch';
import { isNil } from 'lodash';

let refreshAuthenticationPromise;
const dateFormat = 'YYYY-MM-DD';

function log({ type, server, uri, data }) {
    const requestTypeColors = {
        GET: '#61AFFE',
        PUT: '#FCA130',
        POST: '#49CC90',
        DELETE: '#F93E3E',
    };

    if (data) {
        const sanitizedData = { ...data };
        if (sanitizedData.password) {
            sanitizedData.password = '***';
        }
        console.log(
            `%c ${type} ${server}${uri} ${JSON.stringify(sanitizedData) || ''}`,
            `color: ${requestTypeColors[type]}`,
        );
    } else {
        console.log(`%c ${type} ${server}${uri}`, `color: ${requestTypeColors[type]}`);
    }
}

function callHUB_API(type, uri, credentialStore, data) {
    const server = AppConfig.activeEnvironment.hubUrl;
    const tokens = credentialStore.getTokens();
    let headers = {
        Authorization: 'Bearer ' + tokens.accessToken,
    };

    if (
        typeof tokens.accessToken != 'undefined' &&
        tokens.accessToken !== null &&
        hasTokenExpired(tokens.accessToken)
    ) {
        // The access token has expired, so renew the token and set the new tokens in the CredentialStore and
        // as the new header before making a connection again with FlexWeb HUB
        if (!refreshAuthenticationPromise) {
            // check for an existing in-progress request
            // if nothing is in-progress, start a new refresh token request
            refreshAuthenticationPromise = refreshAuthentication(tokens.refreshToken).then((response) => {
                refreshAuthenticationPromise = null; // clear state
                return response; // resolve with the new tokens
            });
        } else {
            console.log('Refresh already requested');
        }

        return refreshAuthenticationPromise.then((response) => {
            const tokens = response.message.data;
            credentialStore.setTokens(tokens.access_token, tokens.refresh_token);
            headers = {
                Authorization: 'Bearer ' + tokens.access_token,
            };
            return doHUBCall(type, server, uri, headers, data);
        });
    }

    return doHUBCall(type, server, uri, headers, data);
}

function doHUBCall(type, server, uri, headers, data) {
    log({ type, server, uri, data });

    headers['Content-Type'] = 'application/json';

    const mypromise = XHRFetch(type, server + uri, JSON.stringify(data), headers)
        .then(
            (response) => {
                const msg = JSON.parse(response.responseText);
                return { error: false, message: msg };
            },
            (error) => ({
                message: 'Network problem',
                error,
            }),
        )
        .then(
            (response) => response,
            (error) => ({
                message: error,
                error,
            }),
        );

    return mypromise;
}

function hasTokenExpired(accessToken) {
    const tokenObject = JSON.parse(atob(accessToken.split('.')[1]));
    const expirationDate = moment(tokenObject.exp * 1000).utc();

    // Refresh 10 seconds early to avoid errors if browser and server are slightly out-of-sync.
    if (expirationDate - 10000 <= moment().utc()) {
        return true;
    }

    return false;
}

// ------------------------------------------------------------

function isNullIfEmpty(value) {
    if (typeof value == 'undefined' || value === null || value.toString().replace(/\s+/, '') === '') {
        return null;
    }

    return value;
}

/*
 * Transformers:
 * Transform the data to Hub-accepted format
 */
function transformLoginData(rawData) {
    const data = {
        hostname: rawData.hostname,
        username: rawData.username,
        password: rawData.password,
    };

    return data;
}

function transformPagination(metaData) {
    // Return empty string if there is no search param
    if (typeof metaData['search'] == 'undefined') {
        metaData['search'] = '';
    }

    if (typeof metaData['meta'] != 'undefined') {
        metaData['meta'].offset =
            metaData['meta'].page * metaData['meta'].resultsPerPage - metaData['meta'].resultsPerPage;
    }

    return metaData;
}

function transformCompanyData(rawData) {
    const data = {
        name: rawData.name,
        debtor_number: rawData.debtorNumber,
    };
    return data;
}

function transformLicenseData(rawData) {
    // Expires on date to datestring or null if it's empty (and thus never expires)
    if (rawData.expiresOn) {
        rawData.expiresOn = moment(rawData.expiresOn).format('YYYY-MM-DD[T]00:00:00[Z]');
    } else {
        rawData.expiresOn = null;
    }

    // Only send the id, description and readOnly state to FlexWeb HUB
    if (typeof rawData.modules != 'undefined' && Object.keys(rawData.modules).length > 0) {
        rawData.modules = Object.values(rawData.modules).map((module) => {
            return {
                id: module.id,
                readonly: module.readOnly,
            };
        });
    }

    const data = {
        company_id: rawData.companyId,
        description: rawData.description.toString(),
        expires_on: rawData.expiresOn !== null ? moment(rawData.expiresOn).format(dateFormat) : null,
        readonly: rawData.readOnly === 'true',
        children_max: rawData.maxAllowedChildren,
        modules: rawData.modules,
    };

    // If the data is meant for a scheduled change, add the scheduled_on date
    if (typeof rawData.isScheduledChange != 'undefined') {
        data.scheduled_on = rawData.scheduledChangeDate;
    }

    return data;
}

function transformApplicationData(rawData) {
    // If rawData contains an old administration_id
    if (rawData.administration_id) {
        rawData.administrationId = rawData.administration_id;
    }

    // If rawData contains an old application_id
    if (rawData.application_id) {
        rawData.applicationId = rawData.application_id;
    }

    if (rawData.websiteId === '') {
        rawData.websiteId = null;
    }

    const data = {
        administrationId: rawData.administrationId,
        is_internal: !!rawData.isInternal,
        applicationId: rawData.applicationId,
        type: rawData.type,
        name: rawData.name,
        status: rawData.status,
        access_type: rawData.accessType,
        website_id: rawData.websiteId,
        application_internal_code: rawData.applicationInternalCode,
    };

    return data;
}

function transformApiUsersData(rawData) {
    // If rawData contains an old administration_id or comes directly from FlexWeb HUB
    if (rawData.administration_id) {
        rawData.administrationId = rawData.administration_id;
    }

    // If rawData contains an old api_user_id or comes directly from FlexWeb HUB
    if (rawData.api_user_id) {
        rawData.apiUserId = rawData.api_user_id;
    }

    // Only send the name of the permitted routing slugs to FlexWeb HUB
    let routingSlugs = null;
    if (typeof rawData.routingSlugs != 'undefined' && Object.keys(rawData.routingSlugs).length > 0) {
        routingSlugs = [];
        Object.values(rawData.routingSlugs).forEach((category) => {
            for (const routingSlug in category) {
                routingSlugs.push(category[routingSlug].name);
            }
        });
    }

    // Transform ip whitelist comma separated string (removing optional whitespace) to array
    let ipWhitelistArray = [];
    if (rawData.ipWhitelist) {
        ipWhitelistArray = rawData.ipWhitelist.trim().split(/\s*,\s*/);
    }

    if (rawData.password === '') {
        rawData.password = null;
    }

    return {
        administrationId: rawData.administrationId,
        application_id: rawData.applicationId,
        apiUserId: rawData.apiUserId,
        name: rawData.name,
        api_token: rawData.apiToken,
        application_type: isNullIfEmpty(rawData.applicationType),
        hostname: isNullIfEmpty(rawData.hostname),
        permitted_slugs: routingSlugs,
        ip_whitelist: ipWhitelistArray,
        configuration_template_id: rawData.usedTemplateId,
        username: rawData.username,
        password: rawData.password,
    };
}

function transformTemplateData(rawData) {
    const routingSlugs = [];
    if (typeof rawData.selectedRoutingSlugs !== 'undefined' && Object.keys(rawData.selectedRoutingSlugs).length > 0) {
        Object.values(rawData.selectedRoutingSlugs).forEach((category) => {
            for (const routingSlug in category) {
                const route = {
                    route: category[routingSlug].name,
                    required: category[routingSlug].required,
                };

                routingSlugs.push(route);
            }
        });
    }

    // Transform ip whitelist comma separated string (removing optional whitespace) to array
    let ipWhitelistArray = [];
    if (rawData.template.ipWhitelist) {
        ipWhitelistArray = rawData.template.ipWhitelist.trim().split(/\s*,\s*/);
    }

    return {
        name: rawData.template.name,
        type: rawData.template.type,
        access_type: rawData.template.accessType,
        application_type: rawData.template.applicationType,
        ip_whitelist: ipWhitelistArray,
        routes: routingSlugs,
    };
}

/**
 * IN: { name: "company_name" }
 * OUT: "&search[name]=company_name"
 * @param search {Object} - Object with all searchables in key/value pairs
 * @param firstOperator {string} - '?' or '&' - Tell the transformSearch function what operator to set on the first searchable
 * @returns {string} - searchable string in Hub Url format
 */
function transformSearch(search, firstOperator = '&') {
    if (typeof search !== 'object' || !search) return '';

    return Object.keys(search)
        .map((key, index) => `${index === 0 ? firstOperator : '&'}search[${key}]=${search[key]}`)
        .join('');
}

// ------------------------------------------------------------

/*
 * Hub API-calls:
 * The API-calls to GET, POST, PUT or DELETE data
 */

export const authenticateLogin = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformLoginData(rawData);

    return doHUBCall('POST', AppConfig.activeEnvironment.hubUrl, '/v2/admin/auth/login', {}, data);
};

export const refreshAuthentication = (refreshToken) => {
    const data = {
        refresh_token: refreshToken,
    };

    return doHUBCall('POST', AppConfig.activeEnvironment.hubUrl, '/v2/admin/auth/refresh', {}, data);
};

export const logout = (credStore) => {
    const tokens = credStore.getTokens();
    const data = {
        refresh_token: tokens.refreshToken,
    };
    return callHUB_API('POST', '/v2/admin/auth/logout', credStore, data);
};

export const getCompanies = (credStore, data) => {
    if (typeof data !== 'undefined' && Object.keys(data).length > 0 && data.constructor === Object) {
        // Transform the data to Hub-accepted format
        const metadata = transformPagination(data);
        const search = transformSearch(data?.meta?.search);
        const limit = metadata['meta'].resultsPerPage;
        const offset = metadata['meta'].offset;

        let url = `/v2/admin/companies?limit=${limit}&offset=${offset}${search}`;

        if (typeof metadata['keyword'] !== 'undefined' && metadata['keyword'] !== '') {
            url += '&search[name]=' + metadata['keyword'];
        }

        return callHUB_API('GET', url, credStore);
    } else {
        return callHUB_API('GET', '/v2/admin/companies?limit=500', credStore);
    }
};

export const getCompany = (credStore, companyId) => {
    return callHUB_API('GET', '/v2/admin/companies/' + companyId, credStore);
};

export const postCompany = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformCompanyData(rawData);

    return callHUB_API('POST', '/v2/admin/companies', credStore, data);
};

export const putCompany = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformCompanyData(rawData);

    return callHUB_API('PUT', '/v2/admin/companies/' + rawData.companyId, credStore, data);
};

export const setLicensePackageLink = (credStore, rawData) => {
    return callHUB_API(
        'PUT',
        '/v2/admin/licenses/' + rawData.licenseId + '/package-links/' + rawData.packageLinkType,
        credStore,
        {
            base_url: rawData.base_url,
            username: rawData.username,
            password: rawData.password === '' ? null : rawData.password,
            sync_base_url: rawData.sync_base_url,
            sync_username: rawData.sync_username,
            sync_password: rawData.sync_password === '' ? null : rawData.sync_password,
        },
    );
};

export const deleteLicensePackageLink = (credStore, rawData) => {
    return callHUB_API(
        'DELETE',
        '/v2/admin/licenses/' + rawData.licenseId + '/package-links/' + rawData.packageLinkType,
        credStore,
    );
};

export const setAdministrationPackageLink = (credStore, rawData) => {
    return callHUB_API(
        'PUT',
        '/v2/admin/administrations/' + rawData.administrationId + '/package-links/' + rawData.packageLinkType,
        credStore,
        {
            dsn: rawData.dsn,
            database_name: rawData.databaseName,
            database_username: rawData.databaseUsername,
            database_password: rawData.databasePassword,
        },
    );
};

export const deleteAdministrationPackageLink = (credStore, rawData) => {
    return callHUB_API(
        'DELETE',
        '/v2/admin/administrations/' + rawData.administrationId + '/package-links/' + rawData.packageLinkType,
        credStore,
    );
};

export const getAdministrations = (credStore, data) => {
    if (typeof data != 'undefined' && Object.keys(data).length > 0 && data.constructor === Object) {
        // Transform the data to Hub-accepted format
        const metadata = transformPagination(data);

        let url =
            '/v2/admin/administrations?limit=' + metadata['meta'].resultsPerPage;

        if (typeof metadata['keyword'] !== 'undefined' && metadata['keyword'] !== '') {
            url += '&search[keyword]=' + metadata['keyword'];
        }

        if (typeof metadata['needsAttention'] !== 'undefined' && metadata['needsAttention'] === true) {
            url += '&search[needs_attention]=1';
        }

        if (typeof metadata['status'] !== 'undefined' && metadata['status'] !== '' && metadata['status'] !== 'all') {
            url += '&search[online]=' + (metadata['status'] === 'online' ? 'true' : 'false');
        }

        if (typeof metadata['type'] !== 'undefined' && metadata['type'] !== '') {
            url += '&search[type]=' + metadata['type'];
        }

        return callHUB_API('GET', url, credStore);
    } else {
        return callHUB_API('GET', '/v2/admin/administrations?limit=100', credStore);
    }
};

export const getAdministration = (credStore, administrationId) => {
    return callHUB_API('GET', '/v2/admin/administrations/' + administrationId, credStore);
};

export const postAdministration = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = {
        name: rawData.name,
        type: rawData.type,
        database_name: rawData.databaseName,
        database_username: rawData.databaseUsername,
        database_password: rawData.databasePassword,
        environment: rawData.environment,
        company_id: rawData.companyId,
        license_id: rawData.licenseId,
        dsn: rawData.dsn,
        hub_version: rawData.hubVersion,
        youko_version: rawData.youkoVersion,
        fir_version: rawData.firVersion,
        konnect_hostname: rawData.konnectHostname,
        redirect_parentapp_to_konnect: false,
        online: false,
    };

    return callHUB_API('POST', '/v2/admin/administrations', credStore, data);
};

export const putAdministration = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    if (typeof rawData.online === 'string') {
        rawData.online = rawData.online === 'true';
    }

    const data = {
        name: rawData.name,
        company_id: rawData.companyId,
        license_id: rawData.licenseId,
        environment: rawData.environment,
        online: rawData.online,
        hub_version: rawData.hubVersion,
        youko_version: rawData.youkoVersion,
        fir_version: rawData.firVersion,
        konnect_hostname: rawData.konnectHostname,
        redirect_parentapp_to_konnect: false,
    };

    return callHUB_API('PUT', '/v2/admin/administrations/' + rawData.administrationId, credStore, data);
};

export const getAdministrationApplications = (credStore, data) => {
    if (typeof data != 'undefined' && Object.keys(data).length > 0 && data.constructor === Object) {
        // Transform the data to Hub-accepted format
        const metadata = transformPagination(data);

        return callHUB_API(
            'GET',
            '/v2/admin/administrations/' +
                data.administrationId +
                '/applications?limit=' +
                metadata['meta'].resultsPerPage +
                '&offset=' +
                metadata['meta'].offset,
            credStore,
        );
    } else {
        return callHUB_API(
            'GET',
            '/v2/admin/administrations/' + data.administrationId + '/applications?limit=100',
            credStore,
        );
    }
};

export const getAdministrationApplication = (credStore, administrationId, applicationId) => {
    return callHUB_API(
        'GET',
        '/v2/admin/administrations/' + administrationId + '/applications/' + applicationId,
        credStore,
    );
};

export const getAdministrationWebsites = (credStore, data) => {
    return callHUB_API('GET', '/v2/admin/administrations/' + data.administrationId + '/websites?limit=100', credStore);
};

export const postAdministrationApplication = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformApplicationData(rawData);

    return callHUB_API('POST', '/v2/admin/administrations/' + data.administrationId + '/applications', credStore, data);
};

export const putAdministrationApplication = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformApplicationData(rawData);

    return callHUB_API(
        'PUT',
        '/v2/admin/administrations/' + data.administrationId + '/applications/' + data.applicationId,
        credStore,
        data,
    );
};

export const deleteAdministrationApplication = (credStore, administrationId, applicationId) => {
    return callHUB_API(
        'DELETE',
        '/v2/admin/administrations/' + administrationId + '/applications/' + applicationId,
        credStore,
    );
};

export const getAdministrationsApiUsers = (credStore, data) => {
    if (typeof data != 'undefined' && Object.keys(data).length > 0 && data.constructor === Object) {
        // Transform the data to Hub-accepted format
        const metadata = transformPagination(data);

        if (!isNil(data.applicationId)) {
            return callHUB_API(
                'GET',
                '/v2/admin/administrations/' +
                    data.administrationId +
                    '/api-users?' +
                    'search[application_id]=' +
                    data.applicationId +
                    '&limit=' +
                    metadata['meta'].resultsPerPage +
                    '&offset=' +
                    metadata['meta'].offset,
                credStore,
            );
        } else {
            return callHUB_API(
                'GET',
                '/v2/admin/administrations/' +
                    data.administrationId +
                    '/api-users?' +
                    '&limit=' +
                    metadata['meta'].resultsPerPage +
                    '&offset=' +
                    metadata['meta'].offset,
                credStore,
            );
        }
    } else {
        return callHUB_API(
            'GET',
            '/v2/admin/administrations' +
                data.administrationId +
                '/api-users?' +
                'search[application_id]=' +
                data.applicationId +
                '&limit=100',
            credStore,
        );
    }
};

export const getAdministrationsApplicationApiUser = (credStore, administrationId, apiUserId) => {
    return callHUB_API('GET', '/v2/admin/administrations/' + administrationId + '/api-users/' + apiUserId, credStore);
};

export const postAdministrationsApplicationApiUser = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformApiUsersData(rawData);

    return callHUB_API('POST', '/v2/admin/administrations/' + data.administrationId + '/api-users', credStore, data);
};

export const putAdministrationsApplicationApiUser = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformApiUsersData(rawData);

    return callHUB_API(
        'PUT',
        '/v2/admin/administrations/' + data.administrationId + '/api-users/' + data.apiUserId,
        credStore,
        data,
    );
};

export const deleteAdministrationsApplicationApiUser = (credStore, administrationId, apiUserId) => {
    return callHUB_API(
        'DELETE',
        '/v2/admin/administrations/' + administrationId + '/api-users/' + apiUserId,
        credStore,
    );
};

export const getLicenses = (credStore) => {
    return callHUB_API('GET', '/v2/admin/licenses?limit=100', credStore);
};

export const getLicense = (credStore, licenseId) => {
    return callHUB_API('GET', '/v2/admin/licenses/' + licenseId + '?expand=scheduled_changes', credStore);
};

export const postLicense = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformLicenseData(rawData);

    return callHUB_API('POST', '/v2/admin/licenses', credStore, data);
};

export const putLicense = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformLicenseData(rawData);

    return callHUB_API('PUT', '/v2/admin/licenses/' + rawData.licenseId, credStore, data);
};

export const postScheduledChangeOfLicense = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformLicenseData(rawData);

    return callHUB_API('POST', '/v2/admin/licenses/' + rawData.licenseId + '/scheduled-changes', credStore, data);
};

export const deleteLicense = (credStore, data) => {
    return callHUB_API('DELETE', '/v2/admin/licenses/' + data.id, credStore, data);
};

export const getModules = (credStore, data) => {
    return callHUB_API('GET', '/v2/admin/modules?limit=100', credStore);
};

export const deleteScheduledLicenseChange = (credStore, data) => {
    return callHUB_API(
        'DELETE',
        '/v2/admin/licenses/' + data.licenseId + '/scheduled-changes/' + data.scheduledChangeId,
        credStore,
        data,
    );
};

export const getRoutingSlugs = (credStore, data) => {
    let metaDataOffset = 0;
    if (typeof data != 'undefined' && Object.keys(data).length > 0 && data.constructor === Object) {
        metaDataOffset = data.offset;
    }
    return callHUB_API('GET', '/v2/admin/documentation?limit=1000&offset=' + metaDataOffset, credStore);
};

export const getTemplates = (credStore, data) => {
    // Transform the data to Hub-accepted format
    const metadata = transformPagination(data);

    return callHUB_API(
        'GET',
        '/v2/admin/configuration-templates?limit=' + metadata.meta.resultsPerPage + '&offset=' + metadata.meta.offset,
        credStore,
    );
};

export const getTemplate = (credStore, templateId) => {
    return callHUB_API('GET', '/v2/admin/configuration-templates/' + templateId, credStore);
};

export const postTemplate = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformTemplateData(rawData);

    return callHUB_API('POST', '/v2/admin/configuration-templates/', credStore, data);
};

export const putTemplate = (credStore, rawData) => {
    // Transform the data to Hub-accepted format
    const data = transformTemplateData(rawData);

    return callHUB_API('PUT', '/v2/admin/configuration-templates/' + rawData.templateId, credStore, data);
};

export const deleteTemplate = (credStore, templateId) => {
    return callHUB_API('DELETE', '/v2/admin/configuration-templates/' + templateId, credStore);
};

export const getRunningTasks = (credStore) => {
    return callHUB_API('GET', '/v2/admin/tasks?search[status]=running', credStore);
};

export const unlockTask = (credStore, id) => {
    return callHUB_API('POST', '/v2/admin/tasks/unlock/' + id, credStore);
};

export const getJobsOverview = (credStore) => {
    return callHUB_API('GET', '/v2/admin/jobs/overview', credStore);
};

export const getFailedJobs = (credStore, data) => {
    if (typeof data != 'undefined' && Object.keys(data).length > 0 && data.constructor === Object) {
        // Transform the data to Hub-accepted format
        const metadata = transformPagination(data);
        return callHUB_API(
            'GET',
            '/v2/admin/failed-jobs?limit=' + metadata['meta'].resultsPerPage + '&offset=' + metadata['meta'].offset,
            credStore,
        );
    } else {
        return callHUB_API('GET', '/v2/admin/failed-jobs?limit=100', credStore);
    }
};

export const getSessionsSourceOverview = (credStore, searchParams) => {
    const search = transformSearch(searchParams, '?');
    return callHUB_API('GET', `/v2/admin/sessions/source-overview${search}`, credStore);
};

export const provisionSSO = (credStore, administrationId) => {
    return callHUB_API('POST', `/v2/admin/administrations/${administrationId}/provision-sso`, credStore);
};
